import { useState } from 'react';
import { Button, Modal } from '@hotjar/hotsauce';
import type { PriceSummary } from '@hotjar/webapp/billing/subscription';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { trackClick } from '@hotjar/webapp/tracking/common';
import { useOwnedAccount } from '@hotjar/account';
import { extractAccountGroupingProps } from '@hotjar/webapp/tracking/utils';
import { ChurnSurvey } from '@hotjar/webapp/features/Billing/ChurnSurvey';
import { isAccountAllowed } from '@hotjar/webapp/domains/common/feature';
import { PlanChanges } from './PlanChanges';
import { PlansCost } from './PlansCost';
import { MODAL_NAME } from '../types';

const CHURN_FORM_NAME = 'churn-form';

interface Props {
  onCancel(): void;
  onChange(clickName: string): void;
  upgradeCount: number;
  downgradeCount: number;
  isChurning?: boolean;
  payNowPrice?: PriceSummary;
  payLaterPrice?: PriceSummary;
  isLoading?: boolean;
}

export function PlanChangeConfirmationSummary({
  onCancel,
  onChange,
  upgradeCount,
  downgradeCount,
  payNowPrice,
  payLaterPrice,
  isLoading,
  isChurning,
}: Props): JSX.Element {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t: tBilling } = useTranslationWithPrefix('billing:plan-change-confirmation');
  const { t: tCommon } = useTranslationWithPrefix('common:actions');
  const { data: account } = useOwnedAccount();
  const isPsd2 = isAccountAllowed('billing_3ds2', account ?? null);

  const handleOnChange = () => {
    onChange(isChurning ? 'Continue' : 'Confirm change');
  };

  return (
    <>
      <Modal.Header title={tBilling('title')} />
      <Modal.Body>
        <PlanChanges
          changes={[
            { type: 'upgrade', count: upgradeCount },
            { type: 'downgrade', count: downgradeCount },
          ]}
        />
        {!isChurning && (
          <PlansCost
            payNowPrice={payNowPrice}
            payLaterPrice={payLaterPrice}
            isPsd2={isPsd2}
            onPopoverClicked={(uiPosition) => {
              trackClick({
                name: `${MODAL_NAME.planChange} popover`,
                uiPosition,
                groupData: extractAccountGroupingProps(account),
              });
            }}
          />
        )}
        {isChurning && (
          <ChurnSurvey
            onPlanChangeSubmit={handleOnChange}
            formId={CHURN_FORM_NAME}
            onIsSubmittingChange={setIsSubmitting}
          />
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button kind="ghost" onClick={onCancel}>
          {tCommon('cancel')}
        </Button>
        {isChurning && (
          <Button form={CHURN_FORM_NAME} type="submit" isLoading={isSubmitting || isLoading}>
            {tCommon('continue')}
          </Button>
        )}
        {!isChurning && (
          <Button isLoading={isLoading} onClick={handleOnChange}>
            {payNowPrice?.total.eq(0) ? tBilling('confirm') : tBilling('confirm-and-pay')}
          </Button>
        )}
      </Modal.Footer>
    </>
  );
}
